// Sino de alertas do cabeçalho (PO1-2194, A2).
//
// Duas variantes do MESMO componente, uma para cada lugar do AdminNavbar:
//   - "desktop": dropdown dentro do <Nav> da direita (antes do avatar).
//   - "mobile": botão fixo no cabeçalho, FORA do collapse, logo antes do "⋮",
//     que abre um painel de tela cheia abaixo do cabeçalho.
// Cada variante só se monta na sua faixa de largura (matchMedia), então nunca há
// dois sinos na tela nem duas consultas de contagem em paralelo. As classes
// d-lg-none / d-none d-lg-block no AdminNavbar reforçam o mesmo corte por CSS.
//
// Contagem: GET /alertas/minhas/contagem a cada 60 s. A lista (10 últimos) é
// recarregada toda vez que o dropdown/painel abre.
//
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import { Dropdown, DropdownMenu, DropdownToggle, Spinner } from "reactstrap";

import { useActiveProduct } from "../../hooks/activeProduct";
import { useFeatures } from "../../hooks/features";
import { contarMinhas, listarMinhas, marcarLido, marcarTodosLidos } from "../../services/alertasApi";
import {
  abrirLink,
  produtoComAlertas,
  rotaAlertas,
  rotaTarefas,
  tempoRelativo,
} from "../../views/Alertas/alertasUtils";

import "./styles.css";

export const INTERVALO_CONTAGEM_MS = 60000;
const LIMITE_LISTA = 10;
// Mesmo corte do navbar `expand="lg"`: abaixo de 992px os itens da direita
// recolhem atrás do "⋮".
const MEDIA_CELULAR = "(max-width: 991.98px)";

function lerCelular() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(MEDIA_CELULAR).matches;
}

function useCelular() {
  const [celular, setCelular] = useState(lerCelular);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(MEDIA_CELULAR);
    const onChange = () => setCelular(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);
  return celular;
}

export function textoBadge(n) {
  if (!n || n <= 0) return null;
  return n > 9 ? "9+" : String(n);
}

function SinoIcone({ naoLidos }) {
  const badge = textoBadge(naoLidos);
  return (
    <span className="alertas-sino__icone">
      <i className="tim-icons icon-bell-55" />
      {badge ? (
        <span className="alertas-sino__badge" data-testid="alertas-sino-badge">
          {badge}
        </span>
      ) : null}
    </span>
  );
}

function ListaItens({ itens, carregando, onClicar, grande }) {
  if (carregando && itens.length === 0) {
    return (
      <div className="alertas-sino__vazio">
        <Spinner size="sm" />
      </div>
    );
  }
  if (itens.length === 0) {
    return <div className="alertas-sino__vazio">Nenhum alerta por aqui.</div>;
  }
  return (
    <ul className={`alertas-sino__lista${grande ? " alertas-sino__lista--grande" : ""}`}>
      {itens.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className={`alertas-sino__item${item.lido ? "" : " alertas-sino__item--nao-lido"}`}
            onClick={() => onClicar(item)}
          >
            <span className="alertas-sino__item-icone">
              <i className={`tim-icons ${item.tipo === "TAREFA" ? "icon-check-2" : "icon-bell-55"}`} />
            </span>
            <span className="alertas-sino__item-corpo">
              <span className="alertas-sino__item-topo">
                <span className="alertas-sino__item-titulo">{item.nome}</span>
                <span className="alertas-sino__item-tempo">{tempoRelativo(item.criadoEm)}</span>
              </span>
              <span className="alertas-sino__item-mensagem">{item.mensagem}</span>
            </span>
            {!item.lido ? <span className="alertas-sino__ponto" aria-label="Não lido" /> : null}
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function AlertasSino({ variant = "desktop", className = "" }) {
  const history = useHistory();
  const location = useLocation();
  const { activeProduct, setActiveProduct } = useActiveProduct();
  const { codes } = useFeatures();
  const celular = useCelular();
  const visivel = variant === "mobile" ? celular : !celular;

  const [aberto, setAberto] = useState(false);
  const [contagem, setContagem] = useState({ naoLidos: 0, tarefasAbertas: 0 });
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [topoPainel, setTopoPainel] = useState(64);
  const botaoRef = useRef(null);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const carregarContagem = useCallback(async () => {
    try {
      const data = await contarMinhas();
      if (!montado.current || !data) return;
      setContagem({
        naoLidos: Number(data.naoLidos) || 0,
        tarefasAbertas: Number(data.tarefasAbertas) || 0,
      });
    } catch (e) {
      // Silencioso: o sino não pode encher a tela de toast a cada minuto.
    }
  }, []);

  const carregarLista = useCallback(async () => {
    setCarregando(true);
    try {
      const data = await listarMinhas({ limite: LIMITE_LISTA });
      if (!montado.current) return;
      setItens((data && data.items) || []);
      if (data && typeof data.naoLidos === "number") {
        const naoLidos = data.naoLidos;
        setContagem((prev) => ({ naoLidos, tarefasAbertas: prev.tarefasAbertas }));
      }
    } catch (e) {
      if (montado.current) setItens([]);
    } finally {
      if (montado.current) setCarregando(false);
    }
  }, []);

  // Polling da contagem, só na variante visível.
  useEffect(() => {
    if (!visivel) return undefined;
    carregarContagem();
    const timer = setInterval(carregarContagem, INTERVALO_CONTAGEM_MS);
    return () => clearInterval(timer);
  }, [visivel, carregarContagem]);

  // Fecha ao mudar de rota (e ao trocar de faixa de largura).
  useEffect(() => {
    setAberto(false);
  }, [location.pathname, visivel]);

  // Painel do celular: trava a rolagem da página por baixo enquanto aberto.
  useEffect(() => {
    if (variant !== "mobile" || !aberto || typeof document === "undefined") return undefined;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [variant, aberto]);

  const abrir = () => {
    if (variant === "mobile" && botaoRef.current) {
      const nav = botaoRef.current.closest ? botaoRef.current.closest("nav") : null;
      const rect = (nav || botaoRef.current).getBoundingClientRect();
      setTopoPainel(Math.max(0, Math.round(rect.bottom)));
    }
    setAberto(true);
    carregarLista();
  };

  const alternar = () => {
    if (aberto) setAberto(false);
    else abrir();
  };

  const produtoDestino = produtoComAlertas(activeProduct, codes);

  const irPara = (rota) => {
    setAberto(false);
    if (!rota) return;
    if (produtoDestino && produtoDestino !== activeProduct) setActiveProduct(produtoDestino);
    history.push(rota);
  };

  const clicarItem = async (item) => {
    setAberto(false);
    if (!item.lido) {
      setItens((prev) => prev.map((i) => (i.id === item.id ? { ...i, lido: true } : i)));
      setContagem((prev) => ({
        naoLidos: Math.max(0, prev.naoLidos - 1),
        tarefasAbertas: prev.tarefasAbertas,
      }));
      try {
        await marcarLido(item.id);
      } catch (e) {
        // Segue para o link mesmo assim; a próxima contagem corrige o número.
      }
    }
    if (item.produto && item.produto !== activeProduct) setActiveProduct(item.produto);
    abrirLink(item.link, history);
  };

  const marcarTodos = async () => {
    try {
      await marcarTodosLidos();
      setItens((prev) => prev.map((i) => ({ ...i, lido: true })));
      setContagem((prev) => ({ naoLidos: 0, tarefasAbertas: prev.tarefasAbertas }));
    } catch (e) {
      carregarContagem();
    }
  };

  if (!visivel) return null;

  const { naoLidos, tarefasAbertas } = contagem;
  const titulo = (
    <span className="alertas-sino__titulo">
      Alertas <span className="alertas-sino__titulo-conta">· {naoLidos} não lidos</span>
    </span>
  );
  const linkMarcarTodos = (
    <button
      type="button"
      className="alertas-sino__link"
      onClick={marcarTodos}
      disabled={naoLidos === 0}
    >
      Marcar todos como lidos
    </button>
  );

  if (variant === "mobile") {
    const painel = aberto ? (
      <div
        className="alertas-sino-painel"
        style={{ top: topoPainel }}
        role="dialog"
        aria-label="Alertas"
      >
        <div className="alertas-sino-painel__cabecalho">
          {titulo}
          <button
            type="button"
            className="alertas-sino-painel__fechar"
            aria-label="Fechar"
            onClick={() => setAberto(false)}
          >
            ×
          </button>
        </div>
        <div className="alertas-sino-painel__acoes">{linkMarcarTodos}</div>
        <ListaItens itens={itens} carregando={carregando} onClicar={clicarItem} grande />
        <div className="alertas-sino-painel__rodape">
          <button type="button" className="alertas-sino__botao-largo" onClick={() => irPara(rotaAlertas(produtoDestino))}>
            Ver todos em Meus alertas
          </button>
          <button type="button" className="alertas-sino__botao-largo" onClick={() => irPara(rotaTarefas(produtoDestino))}>
            Minhas tarefas ({tarefasAbertas})
          </button>
        </div>
      </div>
    ) : null;

    return (
      <>
        <button
          ref={botaoRef}
          type="button"
          className="alertas-sino-mobile"
          aria-label={`Alertas, ${naoLidos} não lidos`}
          aria-expanded={aberto}
          onClick={alternar}
        >
          <SinoIcone naoLidos={naoLidos} />
        </button>
        {painel && typeof document !== "undefined" ? ReactDOM.createPortal(painel, document.body) : painel}
      </>
    );
  }

  return (
    // inNavbar: menu posicionado por CSS (dropdown-menu-right), sem Popper.
    <Dropdown nav inNavbar isOpen={aberto} toggle={alternar} className={`alertas-sino ${className}`.trim()}>
      <DropdownToggle
        color="default"
        nav
        aria-label={`Alertas, ${naoLidos} não lidos`}
        onClick={(e) => e.preventDefault()}
      >
        <SinoIcone naoLidos={naoLidos} />
        <p className="d-lg-none">Alertas</p>
      </DropdownToggle>
      <DropdownMenu className="dropdown-navbar dropdown-app-switcher alertas-sino__menu" right tag="div">
        <div className="alertas-sino__cabecalho">
          {titulo}
          {linkMarcarTodos}
        </div>
        <ListaItens itens={itens} carregando={carregando} onClicar={clicarItem} />
        <div className="alertas-sino__rodape">
          <button type="button" className="alertas-sino__link" onClick={() => irPara(rotaAlertas(produtoDestino))}>
            Ver todos em Meus alertas
          </button>
          <button type="button" className="alertas-sino__link" onClick={() => irPara(rotaTarefas(produtoDestino))}>
            Minhas tarefas ({tarefasAbertas})
          </button>
        </div>
      </DropdownMenu>
    </Dropdown>
  );
}
